import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'antd';
import { StrykerAlertColor } from 'osep-react-antd';
import { useEffect } from 'react';
import { useGitLabMeQuery } from '../../hooks/query/useGitLabMeQuery';
import useAuthenticationCode from '../../hooks/useAuthenticationCode';
import { useGitLabDisconnect } from '../../hooks/useGitLabDisconnect';
import { GITLAB_ME_KEY } from '../../utils/consts';
import config from '../../config';

type Props = { onGitLabConnectedChange?: (connected: boolean) => void };

const GitLabConnectionButton = ({ onGitLabConnectedChange }: Props) => {
  const queryClient = useQueryClient();
  const { disconnectFromGitLab } = useGitLabDisconnect();
  const { startAuthentication } = useAuthenticationCode();
  const { data: gitlabIdentity, isLoading } = useGitLabMeQuery();

  useEffect(() => {
    if (!gitlabIdentity) {
      onGitLabConnectedChange?.(true);
      return;
    }

    if (gitlabIdentity.status === 404) {
      onGitLabConnectedChange?.(false);
    } else {
      onGitLabConnectedChange?.(true);
    }
  }, [gitlabIdentity, onGitLabConnectedChange]);

  const handleDisconnect = async () => {
    if (disconnectFromGitLab) {
      await disconnectFromGitLab();
      queryClient.invalidateQueries({ queryKey: [GITLAB_ME_KEY] });
    }
  };

  if (isLoading) {
    return (
      <Button type="primary" block disabled>
        Connect to GitLab
      </Button>
    );
  }

  if (gitlabIdentity?.status === 404) {
    return (
      <Button
        type="primary"
        block
        style={{
          backgroundColor: StrykerAlertColor.SUCCESS,
          borderColor: StrykerAlertColor.SUCCESS,
          color: 'white',
        }}
        disabled={!startAuthentication}
        onClick={() =>
          startAuthentication &&
          startAuthentication(
            `${config.gitlabAuthUrl}/authorize?client_id=${config.gitlabClientId}&response_type=code&scope=${config.gitlabScope}`,
            `${config.basePath}/gitlab/redirect-receiver`,
          )
        }
      >
        Connect to GitLab
      </Button>
    );
  }

  return (
    <Button
      type="primary"
      block
      style={{
        backgroundColor: StrykerAlertColor.ERROR,
        borderColor: StrykerAlertColor.ERROR,
        color: 'white',
      }}
      disabled={!disconnectFromGitLab}
      onClick={handleDisconnect}
    >
      Disconnect from GitLab
    </Button>
  );
};

export default GitLabConnectionButton;
